import { Injectable, InjectionToken, Inject } from '@angular/core';
import { BookingParametersInterface } from '../../shared/entities/bookingParameters';

export const BROWSER_STORAGE = new InjectionToken<Storage>('Browser Storage', {
  providedIn: 'root',
  factory: () => (typeof window !== 'undefined' ? window.localStorage : null)
});

@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {
  constructor(@Inject(BROWSER_STORAGE) private localStorage) {}

  /**
   * get JSON parsed object behind key, will use fallback if undefined
   * @param key the name of the object in storage
   * @param fallback
   * @returns
   */
  public get<T>(key: string, fallback?: T): T | null {
    const item = this.read(key);
    if (item) {
      return JSON.parse(item);
    }
    if (fallback) {
      return fallback;
    }
  }

  /**
   * write given value as JSON stringified object to storage
   * @param key name of the object in storage
   * @param val your value to store
   * @returns true if successful, false if not
   */
  public set<T>(key: string, val: T): boolean {
    return this.write(key, JSON.stringify(val));
  }

  public write(key: string, val: string): boolean {
    try {
      this.localStorage.setItem(key, val);
      return true;
    } catch {
      return false;
    }
  }

  public read(key: string): string | null {
    try {
      return this.localStorage.getItem(key);
    } catch {
      /* empty */
    }
    return null;
  }

  public remove(key: string): boolean {
    try {
      this.localStorage.removeItem(key);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Removes all items starting with the given prefix
   * @param prefix
   */
  public removeAllPrefixed(prefix: string) {
    const keysToRemove = this.getAllPrefixed(prefix);
    keysToRemove.forEach(keyToRemove => {
      this.remove(keyToRemove);
    });
  }

  public getAllPrefixed(prefix: string): string[] {
    try {
      const keys = [];
      for (let i = 0; i < this.localStorage.length; i++) {
        const lKey = this.localStorage.key(i);
        if (lKey.slice(0, prefix.length) === prefix) {
          keys.push(lKey);
        }
      }
      return keys;
    } catch {
      return [];
    }
  }

  public readBookingParameters(): BookingParametersInterface | null {
    try {
      const bookingParameters = JSON.parse(this.localStorage.getItem('bookingParameters'));
      return bookingParameters.bookingParameters;
    } catch {
      return null;
    }
  }
}
